import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Link as MuiLink, Stack, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { SectionCard } from '../shared/Panel';
import { dateTimeNoSeconds } from '../shared/dateTime';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { useHasAnyPermission } from '../admin/PermissionGate';
import { toErrorMessage } from './preOrderApi';
import { FEATURE_SOURCE_PREVIEW, fetchSource, fetchSourceFileBlob, type PreOrderSource } from './sourcePreviewApi';
import { FEATURE_PRE_ORDER_DOCUMENTS } from './preOrderDocumentsApi';
import { HeicViewer, ImageViewer, PdfViewer, TableViewer, TiffViewer } from './DocumentViewer';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { sessionUserId } from '../authProvider';
import {
  MAIL_FRAME_SANDBOX,
  mailDocument,
  readDocumentOpenPreference,
  readOpenPreference,
  sourceTabs,
  tabForHint,
  writeDocumentOpenPreference,
  writeOpenPreference,
  type SourceHint,
  type SourceTab,
} from './sourcePreviewRules';

/**
 * Podgląd źródła przy prezleceniu (status 28.10.2026 pkt 5; przełącznik
 * `sourcePreview`, S-29.10-01; reguły `sourcePreviewRules.ts`, API
 * `pre-order/source-preview.ts`).
 *
 * Makieta („Podgląd materiałów źródłowych”): po jednej stronie dane
 * prezlecenia, po drugiej źródło — zakładka „Mail” i po jednej na każdy
 * załącznik i plik od klienta. Status 28.10: „pod przyciskiem, żeby nie
 * zaśmiecać ekranu” — dlatego źródło otwiera „Pokaż źródło”, a wybór zostaje
 * zapamiętany w przeglądarce. Treść maila tylko w zamkniętej ramce (bez
 * skryptów i sieci), PDF i obraz na miejscu, reszta — „Pobierz oryginał”.
 *
 * Przyjęcie zleceń, a od decyzji użytkownika 29.10.2026 także planowanie FTL
 * i Trade — tylko odczyt (mail, załączniki, pobranie oryginału). Gość nie widzi
 * ani przycisku, ani odnośników „pokaż w źródle” (API i tak odmawia 403).
 */

/**
 * Od 1280 px dokument stoi obok danych; węziej — pod danymi, na całą
 * szerokość (decyzja Bartka 03.11.2026: „przy wąskim oknie pod formularzem,
 * czytelnie”). Punkty motywu tej szerokości nie mają (lg = 1200, xl = 1536).
 */
export const DOCUMENT_SIDE_BY_SIDE_PX = 1280;
const DOCUMENT_SIDE_BY_SIDE = `@media (min-width:${DOCUMENT_SIDE_BY_SIDE_PX}px)`;

/** Lustro `@RequiresAnyPermission` trasy źródła w API. */
export const SOURCE_PREVIEW_PERMISSIONS = ['ORDER_INTAKE', 'FTL_PLANNING', 'TRADE'] as const;

interface SourcePreviewState {
  /** Przełącznik włączony i konto z przyjęciem zleceń. */
  enabled: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  source: PreOrderSource | null;
  error: string | null;
  tabs: SourceTab[];
  activeKey: string | null;
  setActiveKey: (key: string) => void;
  /** Otwiera źródło na zakładce wskazanej przez pole (albo domyślnej). */
  showInSource: (hint?: SourceHint) => void;
  reload: () => void;
  preOrderId: number;
}

const SourcePreviewContext = createContext<SourcePreviewState | null>(null);

/** Stan podglądu dla prezlecenia — przycisk, panel i odnośniki przy polach czytają go z kontekstu. */
export const SourcePreviewProvider = ({ preOrderId, children }: { preOrderId: number; children: ReactNode }) => {
  const on = useFeature(FEATURE_SOURCE_PREVIEW);
  const allowed = useHasAnyPermission(SOURCE_PREVIEW_PERMISSIONS);
  const enabled = on && allowed;
  // Z `preOrderDocuments` (decyzja Bartka 03.11.2026) dokument jest od razu
  // obok danych; zwinięcie zapamiętujemy osobno dla konta. Bez — jak dotąd:
  // zamknięte, otwiera „Pokaż źródło” (decyzja 29.10.2026).
  const documents = useFeature(FEATURE_PRE_ORDER_DOCUMENTS);
  const userId = sessionUserId();
  const [open, setOpenState] = useState<boolean>(() =>
    documents ? readDocumentOpenPreference(userId) : readOpenPreference(),
  );
  useEffect(() => {
    setOpenState(documents ? readDocumentOpenPreference(userId) : readOpenPreference());
  }, [documents, userId]);
  const [source, setSource] = useState<PreOrderSource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSource(await fetchSource(preOrderId));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [preOrderId]);

  useEffect(() => {
    if (!enabled) return;
    setSource(null);
    void load();
  }, [enabled, load]);

  const tabs = useMemo(() => sourceTabs(source), [source]);
  const setOpen = useCallback(
    (v: boolean) => {
      setOpenState(v);
      if (documents) writeDocumentOpenPreference(userId, v);
      else writeOpenPreference(v);
    },
    [documents, userId],
  );
  const showInSource = useCallback(
    (hint?: SourceHint) => {
      const key = tabForHint(hint, tabs);
      if (key) setActiveKey(key);
      setOpen(true);
    },
    [tabs, setOpen],
  );
  const current = activeKey && tabs.some((t) => t.key === activeKey) ? activeKey : (tabs[0]?.key ?? null);

  const value: SourcePreviewState = {
    enabled,
    open: enabled && open,
    setOpen,
    source,
    error,
    tabs,
    activeKey: current,
    setActiveKey,
    showInSource,
    reload: () => void load(),
    preOrderId,
  };
  return <SourcePreviewContext.Provider value={value}>{children}</SourcePreviewContext.Provider>;
};

/** Stan podglądu albo `null` poza dostawcą / przy wyłączonej funkcji. */
export function useSourcePreview(): SourcePreviewState | null {
  const ctx = useContext(SourcePreviewContext);
  return ctx && ctx.enabled ? ctx : null;
}

/** „Pokaż źródło” / „Ukryj źródło”. */
export const SourcePreviewToggle = () => {
  const ctx = useSourcePreview();
  const documents = useFeature(FEATURE_PRE_ORDER_DOCUMENTS);
  if (!ctx) return null;
  if (documents) {
    // Dokument stoi obok danych od razu — przycisk tylko go zwija i rozwija.
    return (
      <Button
        size="small"
        variant="text"
        startIcon={ctx.open ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
        onClick={() => ctx.setOpen(!ctx.open)}
        data-testid="source-preview-toggle"
        data-mode="document"
        aria-pressed={ctx.open}
      >
        {ctx.open ? 'Zwiń podgląd dokumentu' : `Rozwiń podgląd dokumentu${ctx.tabs.length > 0 ? ` (${ctx.tabs.length})` : ''}`}
      </Button>
    );
  }
  return (
    <Button
      size="small"
      variant={ctx.open ? 'contained' : 'outlined'}
      startIcon={<DescriptionOutlinedIcon />}
      onClick={() => ctx.setOpen(!ctx.open)}
      data-testid="source-preview-toggle"
      aria-pressed={ctx.open}
    >
      {ctx.open ? 'Ukryj źródło' : `Pokaż źródło${ctx.tabs.length > 0 ? ` (${ctx.tabs.length})` : ''}`}
    </Button>
  );
};

/**
 * „pokaż w źródle” przy polu bez wartości albo odczytanym z niską pewnością —
 * otwiera źródło na właściwej zakładce. Bez źródeł (prezlecenie wpisane
 * ręcznie, bez plików) nic nie pokazuje.
 */
export const ShowInSourceLink = ({ hint, field }: { hint?: SourceHint; field?: string }) => {
  const ctx = useSourcePreview();
  if (!ctx || ctx.tabs.length === 0) return null;
  return (
    <MuiLink
      component="button"
      type="button"
      variant="caption"
      onClick={() => ctx.showInSource(hint)}
      sx={{ ml: 0.75, verticalAlign: 'baseline' }}
      data-testid="source-preview-show-in-source"
      data-field={field ?? ''}
    >
      pokaż w źródle
    </MuiLink>
  );
};

/**
 * Układ „źródło obok danych”: przy otwartym źródle dwie kolumny od 1200 px
 * (węziej — źródło nad danymi), przy zamkniętym same dane na całą szerokość.
 */
export const SourceSplit = ({
  children,
  contractPrefix = 'cs',
}: {
  children: ReactNode;
  contractPrefix?: 'cs' | 'adm';
}) => {
  const ctx = useSourcePreview();
  const documents = useFeature(FEATURE_PRE_ORDER_DOCUMENTS);
  if (!ctx || !ctx.open) return <>{children}</>;
  if (documents) {
    // Układ dokumentu ze skanem (03.11.2026, `preOrderDocuments`): dane po
    // lewej (ok. 40%), dokument po prawej (ok. 60%), od 1200 px; węziej —
    // dane nad dokumentem. Dokument przyklejony przy przewijaniu danych.
    return (
      <Box
        sx={{
          display: 'grid',
          // Od 1280 px obok siebie; węziej dokument pod danymi, na całą szerokość.
          gridTemplateColumns: 'minmax(0, 1fr)',
          [DOCUMENT_SIDE_BY_SIDE]: { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)' },
          gap: 2,
          alignItems: 'start',
        }}
        data-testid="source-preview-split"
        data-layout="document"
      >
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        <Box sx={{ minWidth: 0, [DOCUMENT_SIDE_BY_SIDE]: { position: 'sticky', top: 8 } }}>
          <SourcePreviewPanel contractPrefix={contractPrefix} />
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
        gap: 2,
        alignItems: 'start',
      }}
      data-testid="source-preview-split"
    >
      <Box sx={{ minWidth: 0, position: { lg: 'sticky' }, top: { lg: 8 } }}>
        <SourcePreviewPanel contractPrefix={contractPrefix} />
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

const MailView = ({ tab }: { tab: Extract<SourceTab, { type: 'mail' }> }) => {
  const e = tab.email;
  const when = e.sentAt ?? e.receivedAt;
  return (
    <Box data-testid="source-preview-mail" data-email-id={e.id}>
      <Typography variant="subtitle2">{e.subject || '(bez tematu)'}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        Od: {[e.fromName, e.fromAddress ? `<${e.fromAddress}>` : null].filter(Boolean).join(' ') || '—'}
        {when ? ` · ${dateTimeNoSeconds(when)}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        Do: {e.toAddresses.join(', ') || e.sourceMailbox}
        {e.ccAddresses.length > 0 ? ` · DW: ${e.ccAddresses.join(', ')}` : ''}
      </Typography>
      <iframe
        title="Treść wiadomości"
        srcDoc={mailDocument(e.bodyHtml, e.bodyText)}
        sandbox={MAIL_FRAME_SANDBOX}
        referrerPolicy="no-referrer"
        data-testid="source-preview-mail-frame"
        style={{
          marginTop: 8,
          width: '100%',
          height: 560,
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          background: '#fff',
        }}
      />
      {e.bodyHtml ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Treść pokazana bez skryptów i bez obrazów z internetu.
        </Typography>
      ) : null}
    </Box>
  );
};

async function saveToDisk(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Plik z podglądem z przełącznikiem `preOrderDocuments` (03.11.2026):
 * o sposobie pokazania decyduje typ zawartości z naszej trasy (API
 * rozpoznaje plik po pierwszych bajtach) — zawartość niezgodna
 * z rozszerzeniem przychodzi jako dane binarne i idzie tylko do pobrania.
 */
const DocumentBody = ({ blob, url, filename }: { blob: Blob; url: string; filename: string }) => {
  const type = blob.type.toLowerCase();
  if (type === 'application/pdf') return <PdfViewer url={url} filename={filename} />;
  if (type === 'image/tiff') return <TiffViewer blob={blob} filename={filename} />;
  if (type.startsWith('image/')) return <ImageViewer src={url} alt={filename} />;
  return (
    <Alert severity="warning" data-testid="source-preview-mismatch">
      Zawartość pliku nie zgadza się z jego rozszerzeniem — nie pokazujemy go w aplikacji. Pobierz oryginał tylko wtedy,
      gdy ufasz nadawcy.
    </Alert>
  );
};

const FileView = ({ tab, preOrderId }: { tab: Extract<SourceTab, { type: 'file' }>; preOrderId: number }) => {
  const { file, origin } = tab;
  // Arkusz i HEIC mają własne trasy podglądu (tabela, obraz przeliczony na serwerze).
  const viewed =
    file.preview !== undefined && file.preview !== 'none' && file.preview !== 'table' && file.preview !== 'heic';
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file.inline && !viewed) return undefined;
    let objectUrl: string | null = null;
    let alive = true;
    setUrl(null);
    setBlob(null);
    setFailed(false);
    fetchSourceFileBlob(preOrderId, origin, file.id)
      .then((b) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(b);
        setBlob(b);
        setUrl(objectUrl);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Klucz zakładki wystarcza — plik źródła się nie zmienia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOrderId, tab.key, file.inline]);

  const download = async () => {
    setBusy(true);
    try {
      await saveToDisk(await fetchSourceFileBlob(preOrderId, origin, file.id), file.filename);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="source-preview-file" data-file-kind={file.kind} data-inline={file.inline ? '1' : '0'}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
          {file.filename}
        </Typography>
        <Chip size="small" variant="outlined" label={[file.kindLabel, file.sizeLabel].filter(Boolean).join(' · ')} />
        <Chip size="small" variant="outlined" label={origin === 'client' ? 'plik od klienta' : 'załącznik maila'} />
      </Stack>
      {failed ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Nie udało się otworzyć pliku „{file.filename}”. Pozostałe zakładki działają; spróbuj pobrać oryginał.
        </Alert>
      ) : null}
      {file.preview === 'heic' ? (
        <HeicViewer preOrderId={preOrderId} origin={origin} fileId={file.id} filename={file.filename} />
      ) : null}
      {file.preview === 'table' ? (
        <TableViewer preOrderId={preOrderId} origin={origin} fileId={file.id} />
      ) : null}
      {viewed && !failed ? (
        !url || !blob ? (
          <CircularProgress size={20} />
        ) : (
          <DocumentBody blob={blob} url={url} filename={file.filename} />
        )
      ) : null}
      {file.preview === undefined && file.inline && !failed ? (
        !url ? (
          <CircularProgress size={20} />
        ) : file.kind === 'image' ? (
          <Box
            component="img"
            src={url}
            alt={file.filename}
            data-testid="source-preview-image"
            sx={{
              maxWidth: '100%',
              maxHeight: 620,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        ) : (
          <Box
            component="iframe"
            src={url}
            title={file.filename}
            data-testid="source-preview-pdf"
            sx={{
              width: '100%',
              height: 620,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        )
      ) : null}
      {(file.preview === undefined && !file.inline) || file.preview === 'none' ? (
        <Alert severity="info" data-testid="source-preview-no-preview">
          {file.note}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        {(file.preview === undefined && file.inline) || viewed || file.preview === 'heic' ? (
          <Typography variant="caption" color="text.secondary">
            {file.note}
          </Typography>
        ) : null}
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={busy}
          onClick={() => void download()}
          data-testid="source-preview-download"
        >
          Pobierz oryginał
        </Button>
      </Stack>
    </Box>
  );
};

/** „Poprzedni / następny” między materiałami źródła (03.11.2026, `preOrderDocuments`). */
const FileStepper = () => {
  const ctx = useSourcePreview();
  if (!ctx) return null;
  const i = ctx.tabs.findIndex((t) => t.key === ctx.activeKey);
  const go = (d: number) => {
    const next = ctx.tabs[i + d];
    if (next) ctx.setActiveKey(next.key);
  };
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1 }} data-testid="source-preview-stepper">
      <IconButton size="small" aria-label="Poprzedni materiał" disabled={i <= 0} onClick={() => go(-1)} data-testid="source-preview-prev">
        <NavigateBeforeIcon fontSize="small" />
      </IconButton>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {`${i + 1} z ${ctx.tabs.length}`}
      </Typography>
      <IconButton size="small" aria-label="Następny materiał" disabled={i >= ctx.tabs.length - 1} onClick={() => go(1)} data-testid="source-preview-next">
        <NavigateNextIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

/** Panel źródła: zakładki i podgląd wybranej. */
export const SourcePreviewPanel = ({ contractPrefix = 'cs' }: { contractPrefix?: 'cs' | 'adm' }) => {
  const ctx = useSourcePreview();
  if (!ctx) return null;
  const { tabs, activeKey, source, error } = ctx;
  const active = tabs.find((t) => t.key === activeKey) ?? null;
  return (
    <SectionCard title="Źródło">
      <Box data-testid="source-preview-panel">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!source && !error ? <CircularProgress size={20} /> : null}
        {source && tabs.length === 0 ? (
          <Alert severity="info" data-testid="source-preview-empty">
            Prezlecenie nie ma maila ani plików od klienta — nie ma czego pokazać.
            {source.filesEnabled ? ' Plik, z którego wpisano dane, dołączysz w sekcji „Pliki od klienta”.' : ''}
          </Alert>
        ) : null}
        {tabs.length > 0 ? (
          <>
            {/* Zakładki jak w makiecie (identyfikatory `…-zlecenie-zrodla-zakladka-<n>`). */}
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }} role="tablist">
              {tabs.map((t, i) => (
                <Chip
                  key={t.key}
                  size="small"
                  role="tab"
                  aria-selected={t.key === activeKey}
                  label={t.label}
                  icon={t.type === 'mail' ? <ManageSearchIcon /> : undefined}
                  color={t.key === activeKey ? 'primary' : 'default'}
                  variant={t.key === activeKey ? 'filled' : 'outlined'}
                  onClick={() => ctx.setActiveKey(t.key)}
                  // Pełne identyfikatory w jednej linii z data-testid — rejestr ścieżek szuka ich w kodzie.
                  data-testid={contractPrefix === 'adm' ? `adm-zlecenie-zrodla-zakladka-${i}` : `cs-zlecenie-zrodla-zakladka-${i}`}
                  data-tab-key={t.key}
                  sx={{ maxWidth: 260 }}
                />
              ))}
            </Stack>
            {source?.documents && tabs.length > 1 ? <FileStepper /> : null}
            {active?.type === 'mail' ? <MailView key={active.key} tab={active} /> : null}
            {active?.type === 'file' ? <FileView key={active.key} tab={active} preOrderId={ctx.preOrderId} /> : null}
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Źródło jest tylko do odczytu — poprawki wprowadzasz w danych prezlecenia.
            </Typography>
          </>
        ) : null}
      </Box>
    </SectionCard>
  );
};
